// Mini-writer de PDF SIN dependencias (el stack es cerrado): construye un
// documento PDF 1.4 donde cada página es una imagen JPEG a página completa.
//
// Suficiente y suficiente A PROPÓSITO: las páginas se dibujan en canvas
// (misma calidad que el PNG del supervisor) y acá solo se empaquetan. JPEG y
// no PNG porque el PDF lo soporta nativo vía DCTDecode — un PNG habría que
// decodificarlo a píxeles crudos.
//
// Estructura generada: catalog → pages → por página {page, contents, XObject
// imagen} → xref → trailer. Offsets contados en BYTES (el JPEG es binario).

interface PaginaJpeg {
  /** dataURL 'data:image/jpeg;base64,...' del canvas. */
  dataUrl: string;
  /** Dimensiones del MediaBox en puntos (px CSS del canvas van 1:1). */
  w: number;
  h: number;
}

function base64ABytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

const enc = new TextEncoder();

export function pdfDesdeJpegs(paginas: PaginaJpeg[]): Blob {
  const partes: Uint8Array[] = [];
  let offset = 0;
  const offsets: number[] = []; // offset[i] = byte donde inicia el objeto i+1

  const push = (s: string | Uint8Array): void => {
    const bytes = typeof s === 'string' ? enc.encode(s) : s;
    partes.push(bytes);
    offset += bytes.length;
  };
  const pushObj = (num: number, cuerpo: string): void => {
    offsets[num - 1] = offset;
    push(`${num} 0 obj\n${cuerpo}\nendobj\n`);
  };

  push('%PDF-1.4\n%âãÏÓ\n');

  // Numeración: 1 = catalog, 2 = pages, luego por página i (0-based):
  //   page = 3 + i*3 · contents = 4 + i*3 · imagen = 5 + i*3
  const pageObj = (i: number) => 3 + i * 3;
  const totalObjs = 2 + paginas.length * 3;

  pushObj(1, '<< /Type /Catalog /Pages 2 0 R >>');
  pushObj(
    2,
    `<< /Type /Pages /Count ${paginas.length} /Kids [${paginas
      .map((_, i) => `${pageObj(i)} 0 R`)
      .join(' ')}] >>`,
  );

  paginas.forEach((p, i) => {
    const jpeg = base64ABytes(p.dataUrl.split(',')[1] ?? '');
    // La imagen se dibuja ocupando todo el MediaBox (cm = escala w/h).
    const contenido = `q ${p.w} 0 0 ${p.h} 0 0 cm /Im0 Do Q`;
    pushObj(
      pageObj(i),
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${p.w} ${p.h}] ` +
        `/Resources << /XObject << /Im0 ${pageObj(i) + 2} 0 R >> ` +
        `/ProcSet [/PDF /ImageC] >> /Contents ${pageObj(i) + 1} 0 R >>`,
    );
    pushObj(pageObj(i) + 1, `<< /Length ${contenido.length} >>\nstream\n${contenido}\nendstream`);
    // El JPEG del canvas va a doble resolución (scale 2) → Width/Height reales.
    offsets[pageObj(i) + 2 - 1] = offset;
    push(
      `${pageObj(i) + 2} 0 obj\n<< /Type /XObject /Subtype /Image ` +
        `/Width ${p.w * 2} /Height ${p.h * 2} /ColorSpace /DeviceRGB ` +
        `/BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`,
    );
    push(jpeg);
    push('\nendstream\nendobj\n');
  });

  // ── xref + trailer ──
  const xrefOffset = offset;
  let xref = `xref\n0 ${totalObjs + 1}\n0000000000 65535 f \n`;
  for (let i = 0; i < totalObjs; i++) {
    xref += `${String(offsets[i] ?? 0).padStart(10, '0')} 00000 n \n`;
  }
  push(xref);
  push(`trailer\n<< /Size ${totalObjs + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`);

  return new Blob(partes as BlobPart[], { type: 'application/pdf' });
}

/** Descarga un PDF armado con `pdfDesdeJpegs`. */
export function descargarPdf(paginas: PaginaJpeg[], nombreArchivo: string): void {
  const blob = pdfDesdeJpegs(paginas);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombreArchivo;
  a.click();
  URL.revokeObjectURL(url);
}
